
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Camera, Image, TrendingUp } from "lucide-react";
import { formatCurrency } from "@/lib/utils";

interface Deal {
  total_cameras?: number | null;
  total_images?: number | null;
  value?: number | null;
}

interface StatsTabProps {
  deals: Deal[];
  isLoading: boolean;
  title?: string;
}

export const StatsTab = ({ deals, isLoading, title = "Statistics" }: StatsTabProps) => {
  if (isLoading) {
    return (
      <div className="p-6 text-center text-slate-600">
        Loading statistics...
      </div>
    );
  }

  const totalCameras = deals.reduce((sum, deal) => sum + (deal.total_cameras || 0), 0);
  const totalImages = deals.reduce((sum, deal) => sum + (deal.total_images || 0), 0);
  const totalValue = deals.reduce((sum, deal) => sum + (deal.value || 0), 0);

  return (
    <div className="space-y-6 p-6">
      <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-700">Total Cameras</CardTitle>
              <div className="bg-blue-100 p-2 rounded-lg">
                <Camera className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-800">{totalCameras.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-700">Total Images</CardTitle>
              <div className="bg-green-100 p-2 rounded-lg">
                <Image className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-800">{totalImages.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-700">Total Value</CardTitle>
              <div className="bg-purple-100 p-2 rounded-lg">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-800">{formatCurrency(totalValue)}</div>
          </CardContent>
        </Card>
      </div>

      <div className="text-sm text-slate-500 text-center">
        Statistics calculated from {deals.length} deal{deals.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};
